/**
 * Theme is a plain external store rather than a Zustand slice — Zustand is
 * reserved for auth. The `data-theme` attribute is what tokens.css reads.
 */

import { useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'exchange.theme';
const listeners = new Set<() => void>();

let mode: ThemeMode = readStoredMode();

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private mode or blocked storage — fall through to the system default.
  }
  return 'system';
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(current: ThemeMode): 'light' | 'dark' {
  if (current === 'system') return prefersDark() ? 'dark' : 'light';
  return current;
}

function apply(): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(mode));
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Persisting is a convenience; the session still gets the right theme.
  }
  apply();
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemeMode {
  return mode;
}

/** Call once at startup: applies the stored theme and follows OS changes. */
export function initTheme(): void {
  apply();
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', () => {
    if (mode === 'system') {
      apply();
      emit();
    }
  });
}

export function useThemeMode(): [ThemeMode, (next: ThemeMode) => void] {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [current, setThemeMode];
}
